import { CSSProperties, ForwardedRef, forwardRef, useEffect, useState } from 'react';
import MiddleEllipsis from 'react-middle-ellipsis';
import styles from './File.module.scss';
import DownloadButton from '../shared/DownloadButton';
import Spinner from '../shared/Spinner/Spinner';
import FilePreviewOverlay from '../shared/FilePreviewOverlay/FilePreviewOverlay';
import { isPreviewable } from '../shared/FilePreviewViewer/FilePreviewViewer';
import { ServiceDownload } from '../../services/service-download';
import { ReactComponent as CircleXIconFilled } from '../../assets/svg/circleXIconFilled.svg';
import { ReactComponent as FileIconBackground } from '../../assets/svg/file-icon-background.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/downloadIcon.svg';
import { FileScope } from '../../util/appEnum';
import { ScopeOptions } from '../../models/global.model';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';

type Props = {
  fileName: string,
  disabled?: boolean,
  file?: string,
  url?: string,
  onRemove?: () => void,
  style?: CSSProperties,
  scope?: FileScope,
  scopeOptions?: ScopeOptions,
  blob?: Blob,
  className?: string,
  /** Called after the file was downloaded. */
  onDownload?: () => void,
  /** Called when the file is opened in the preview overlay. */
  onPreview?: () => void,
}

const File = forwardRef(function File({ style, fileName = '', disabled, file, url, onRemove, scope, scopeOptions, blob, className, onDownload, onPreview, }: Props, ref: ForwardedRef<HTMLDivElement>) {
  const [t] = useTranslation('files');
  const [icon, setIcon] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const checkImage = (src: string, bad: () => void) => {
    const img = new Image();
    img.onerror = bad;
    img.src = src;
  }

  const getFileExtension = (fileName: string) => {
    return fileName?.slice(((fileName.lastIndexOf('.') - 1) >>> 0) + 2);
  }

  useEffect(() => {
    const images = importAll(require.context('../../assets/file-icons/', false, /\.(png|jpe?g|svg)$/));
    const tempFileIcon = images[`${getFileExtension(fileName.toLowerCase())}.svg`];
    const tempIcon = tempFileIcon ? tempFileIcon : images['blank.svg'];
    setIcon(tempIcon);
    checkImage(tempIcon, () => {
      setIcon(images['blank.svg']);
    });
  }, [])


  const importAll = (source: { keys: () => string[] } & ((filename: string) => string)) => {
    const images: { [filename: string]: string } = {};
    source.keys().forEach(item => { images[item.replace('./', '')] = source(item); });
    return images
  }

  const getFilename = (file: string) => {
    return file?.slice(((file.lastIndexOf('/') - 1) >>> 0) + 2) || file;
  }

  const fileUrl = url || (file && scope && !_.isEmpty(scopeOptions) && ServiceDownload.getFileDownloadURL(file, scope, scopeOptions)) || '';
  const canPreview = !blob && !!fileUrl && isPreviewable(fileName);

  return (
    <>
      <DownloadButton
        disabled={disabled}
        url={fileUrl}
        blob={blob}
        fileName={fileName}
        render={({ downloading, progress, onClick }) => {
          const download = onClick && (async () => {
            await onClick();
            onDownload?.();
          });
          const openPreview = () => {
            setShowPreview(true);
            onPreview?.();
          };

          return (
            <div
              ref={ref}
              className={`${className} ${styles.container} ${disabled ? styles.disabled : ''}`}
              style={style}
              onClick={canPreview ? openPreview : download}
            >
              {onRemove && (
                <CircleXIconFilled
                  onClick={e => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  className={styles.removeIcon}
                />
              )}
              <div className={styles.fileIconContainer}>
                <FileIconBackground />
                <img src={icon} />
              </div>
              <div className={styles.textContainer} style={{ width: !downloading ? '95%' : 'calc(95% - 40px)' }}>
                <MiddleEllipsis>
                  <span>{getFilename(fileName)}</span>
                </MiddleEllipsis>
                <span>{getFileExtension(fileName?.toLowerCase())?.toUpperCase()}</span>
              </div>

              {downloading && <Spinner isHidden={!downloading} size='xxs' progress={progress} />}
              {canPreview && !downloading && (
                <div
                  className={styles.downloadIcon}
                  onClick={e => { e.stopPropagation(); download?.(); }}
                  title={t('button.download', 'Ladda ner')}
                >
                  <DownloadIcon />
                </div>
              )}
            </div>
          );
        }}
      />
      {showPreview && (
        <FilePreviewOverlay
          url={fileUrl}
          fileName={fileName}
          onClose={() => setShowPreview(false)}
          onDownload={onDownload}
        />
      )}
    </>
  )
})

export default File;
